import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FormSection, Button, ErrorState, useToastActions } from '@airclou/ui'
import { Mail, Plus, Edit2, CreditCard, Landmark, Wallet, ExternalLink } from 'lucide-react'
import { getBillingAccount, openBillingPortal } from '../../../services/billing'
import type { BillingAccount, PaymentMethodSummary } from '../../../services/billing'
import { usePageRestored } from '../../../hooks/usePageRestored'

/**
 * Billing email, saved payment methods and billing address — read live from
 * Stripe, edited in Stripe.
 *
 * Every Edit and Add here opens Stripe's hosted billing portal and comes back
 * to this tab. That keeps card data off our origin and leaves the awkward
 * cases (removing the only card on a live subscription, 3-D Secure on a new
 * one) to the system that owns them. What the portal changed is read back on
 * the next load; nothing is written or cached here.
 */

/** Which control opened the portal, so only that control reads "Opening…". */
type PortalTask = 'details' | 'payment' | 'manage' | 'address'

function PaymentMethodIcon({ type }: { type: string }) {
  const Icon = type === 'card' ? CreditCard : type === 'us_bank_account' || type === 'sepa_debit' ? Landmark : Wallet
  return <Icon className="h-5 w-5 text-muted-foreground" />
}

function PaymentMethodRow({ method }: { method: PaymentMethodSummary }) {
  const { t } = useTranslation('dashboard')
  const hasExpiry = method.expMonth != null && method.expYear != null
  return (
    <div className="flex items-center justify-between p-4 border border-border rounded-lg">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
          <PaymentMethodIcon type={method.type} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium capitalize">{method.brand ?? method.type.replace(/_/g, ' ')}</p>
            {method.last4 && <span className="text-sm text-muted-foreground">•••• {method.last4}</span>}
            {method.isDefault && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/15 text-primary dark:bg-primary/25 dark:text-[hsl(var(--primary-hover))]">
                {t('billing.default')}
              </span>
            )}
          </div>
          {hasExpiry && (
            <p className="text-sm text-muted-foreground">
              {t('billing.expires')} {String(method.expMonth).padStart(2, '0')}/{method.expYear}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export function PaymentMethodsTab() {
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const toast = useToastActions()

  const [account, setAccount] = useState<BillingAccount | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [opening, setOpening] = useState<PortalTask | null>(null)

  async function load() {
    setIsLoading(true)
    setError(null)
    try {
      setAccount(await getBillingAccount())
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t('billing.accountLoadFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  // On success the browser leaves for Stripe, so `opening` stays set and the
  // buttons stay disabled until the portal brings it back (or Back does —
  // see below).
  async function openPortal(task: PortalTask, flow?: 'payment_method_update') {
    setOpening(task)
    try {
      await openBillingPortal({ flow, returnTab: 'payment-methods' })
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : t('billing.portalFailed'))
      setOpening(null)
    }
  }

  // Back from the portal instead of its return link: re-arm the buttons and
  // re-read, since a card may have been added or removed there.
  usePageRestored(() => {
    setOpening(null)
    void load()
  })

  const hasAccount = account?.hasBillingAccount === true
  // Nothing to edit until Stripe knows the organization, and nothing while a
  // portal session is already being made.
  const portalDisabled = isLoading || !hasAccount || opening !== null
  const portalLabel = (task: PortalTask, label: string) => (opening === task ? t('billing.openingPortal') : label)

  const skeleton = <div className="h-5 w-48 animate-pulse rounded bg-muted" aria-hidden="true" />
  const body = (content: ReactNode) => (isLoading ? skeleton : content)

  if (error) {
    return (
      <ErrorState
        title={t('billing.accountLoadFailed')}
        description={error}
        action={
          <Button variant="outline" size="sm" onClick={() => void load()}>
            {t('common.retry')}
          </Button>
        }
      />
    )
  }

  const address = account?.address
  const addressLines = address
    ? [
        address.line1,
        address.line2,
        [address.city, address.state, address.postalCode].filter(Boolean).join(', '),
        address.country,
      ].filter((line): line is string => Boolean(line && line.trim()))
    : []

  return (
    <div className="space-y-6">
      {/* Where this data lives, said once at the top rather than on every button. */}
      <p className="text-sm text-muted-foreground">{t('billing.managedByStripe')}</p>

      {/* Billing Information */}
      <FormSection
        title={t('billing.billingInformation')}
        actions={
          <Button variant="outline" size="sm" disabled={portalDisabled} onClick={() => void openPortal('details')}>
            <Edit2 className="h-4 w-4 mr-2" />
            {portalLabel('details', t('billing.edit'))}
          </Button>
        }
      >
        {body(
          hasAccount ? (
            <div className="flex items-center gap-3 p-4 border border-border rounded-lg">
              <div className="w-10 h-10 bg-primary/15 dark:bg-primary/25 rounded-full flex items-center justify-center">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm text-muted-foreground">{t('billing.billingEmail')}</p>
                <p className="text-base">{account?.billingEmail ?? '—'}</p>
                {account?.billingName && <p className="text-sm text-muted-foreground">{account.billingName}</p>}
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-muted-foreground">{t('billing.noBillingDetailsYet')}</p>
              <Button variant="outline" size="sm" onClick={() => navigate('/billing/subscription')}>
                {t('billingPlans.seePlans')}
              </Button>
            </div>
          )
        )}
      </FormSection>

      {/* Payment Methods */}
      <FormSection
        title={t('billing.paymentMethods')}
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={portalDisabled}
            onClick={() => void openPortal('payment', 'payment_method_update')}
          >
            <Plus className="h-4 w-4 mr-2" />
            {portalLabel('payment', t('billing.addPaymentMethod'))}
          </Button>
        }
        contentClassName="space-y-3"
      >
        {body(
          account && account.paymentMethods.length > 0 ? (
            <>
              {account.paymentMethods.map((method) => (
                <PaymentMethodRow key={method.id} method={method} />
              ))}
              {/* Removing a method or changing the default is the portal's home page, not the add flow. */}
              <button
                type="button"
                disabled={portalDisabled}
                onClick={() => void openPortal('manage')}
                className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline disabled:opacity-50"
              >
                <ExternalLink className="h-4 w-4" />
                {portalLabel('manage', t('billing.manageInPortal'))}
              </button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t('billing.noPaymentMethods')}</p>
          )
        )}
      </FormSection>

      {/* Billing Address */}
      <FormSection
        title={t('billing.billingAddress')}
        actions={
          <Button variant="outline" size="sm" disabled={portalDisabled} onClick={() => void openPortal('address')}>
            <Edit2 className="h-4 w-4 mr-2" />
            {portalLabel('address', t('billing.edit'))}
          </Button>
        }
      >
        {body(
          addressLines.length > 0 ? (
            <div className="text-sm text-muted-foreground space-y-1">
              {addressLines.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('billing.noBillingAddress')}</p>
          )
        )}
      </FormSection>
    </div>
  )
}
